/* ABConvert storefront price presentation.

   ABConvert changes the price number itself (its own card processor on PDP /
   standard cards). This script adds the presentation layer that ABConvert does
   NOT handle:
     - Grundpreis (€/kg) — scaled by testPrice/controlPrice (linear in price).
     - "New price" badge (green/red by test group name), shown when the assigned
       group price differs from the control price.
     - The original price struck-through (from control group) when the product
       has no compare-at price.
     - On PDP: a promo row "↓ X% niedriger | Dauerhaft günstiger" + the price
       coloured red.

   Group → colour: by ABConvert group name ("New Price Red" / "New Price Green").
   Size-pill product cards render their own price in grid-view-item.js and reuse
   the builders exposed here via window.AbcUI. */

(function () {
  'use strict';

  /* ---------- money helpers ---------- */

  function parseMoney(str) {
    if (str == null) return NaN;
    var t = String(str).replace(/[^\d.,]/g, '');
    if (!t) return NaN;
    var dec = t.match(/([.,])(\d{1,2})$/);
    var norm;
    if (dec) {
      var sep = dec[1];
      var thou = sep === ',' ? '.' : ',';
      norm = t.split(thou).join('').replace(sep, '.');
    } else {
      norm = t.replace(/[.,]/g, '');
    }
    return parseFloat(norm);
  }

  function formatLikeTemplate(template, amount) {
    var n = Number(amount);
    if (template == null || !isFinite(n)) return template;
    var t = String(template);
    var numMatch = t.match(/[\d.,]+/);
    if (!numMatch) return t;
    var numToken = numMatch[0];
    var decMatch = numToken.match(/([.,])(\d{1,2})$/);
    var decSep = decMatch ? decMatch[1] : '.';
    var thouSep = decSep === ',' ? '.' : ',';
    var abs = Math.abs(n);
    var intPart = Math.floor(abs).toString().replace(/\B(?=(\d{3})+(?!\d))/g, thouSep);
    var frac = Math.round((abs - Math.floor(abs)) * 100).toString().padStart(2, '0');
    var newNum = (n < 0 ? '-' : '') + intPart + decSep + frac;
    return t.replace(numToken, newNum);
  }

  /* ---------- ABConvert API ---------- */

  function abcGroupIndex(productId) {
    var dict = window.abconvertPriceCartTransformTestDict;
    var entry = dict && dict[productId];
    if (!entry) return null;
    var groupIndex;
    try {
      var groups = JSON.parse(localStorage.getItem('abconvert-experiment-to-group-index') || '{}');
      groupIndex = groups ? groups[entry.experimentId] : undefined;
    } catch (e) {}
    if ((groupIndex === undefined || groupIndex === null) &&
        window.abconvert && window.abconvert.user && window.abconvert.user.getAssignedGroup) {
      var g = window.abconvert.user.getAssignedGroup(entry.experimentId);
      groupIndex = (g && typeof g === 'object') ? g.groupIndex : g;
    }
    return (groupIndex === undefined || groupIndex === null) ? null : groupIndex;
  }

  function abcPriceForGroup(variantId, groupIndex) {
    var api = window.abconvert && window.abconvert.price;
    if (!api || groupIndex === null || groupIndex === undefined) return null;
    var country = (window.Shopify && window.Shopify.country) || '';
    var res = country ? api.getByVariant(variantId, groupIndex, { country }) : null;
    if (!res) res = api.getByVariant(variantId, groupIndex);
    return res || null;
  }

  function abcReady() {
    return window.ABCONVERT_PRICE_SYNC_ENABLED !== false &&
           !!(window.abconvert && window.abconvert.utils &&
              window.abconvert.utils.isReady && window.abconvert.utils.isReady());
  }

  // Test/control prices, colour, discount % for a variant — or null.
  function variantInfo(productId, variantId) {
    if (!productId || !variantId) return null;
    var gi = abcGroupIndex(productId);
    if (gi === null) return null;
    var test = abcPriceForGroup(variantId, gi);
    var control = abcPriceForGroup(variantId, 0);
    if (!test || !control || control.price == null) return null;
    var t = Number(test.price), c = Number(control.price);
    return {
      isNew: isFinite(t) && isFinite(c) && t !== c,
      test: test,
      control: control,
      color: resolveColor(productId, gi, test.groupName),
      percent: (isFinite(t) && isFinite(c) && c > 0) ? Math.round((1 - t / c) * 100) : null
    };
  }

  /* ---------- badge builders (shared with grid-view-item.js) ---------- */

  // Returns 'green' / 'red' only when the name actually contains a colour keyword.
  function colorFromName(name) {
    var s = String(name || '').toLowerCase();
    if (s.indexOf('green') >= 0 || s.indexOf('grün') >= 0 || s.indexOf('grun') >= 0) return 'green';
    if (s.indexOf('red') >= 0 || s.indexOf('rot') >= 0) return 'red';
    return null;
  }

  function colorForGroupName(name) {
    return colorFromName(name) || 'red';
  }

  // Robust colour: group name keyword → getAvailableGroups name → index fallback.
  // This experiment's order is Old=0, New Price Red=1, New Price Green=2.
  function resolveColor(productId, gi, groupName) {
    var c = colorFromName(groupName);
    if (c) return c;
    try {
      var dict = window.abconvertPriceCartTransformTestDict || {};
      var exp = dict[productId] && dict[productId].experimentId;
      var groups = (exp && window.abconvert && window.abconvert.user && window.abconvert.user.getAvailableGroups)
        ? window.abconvert.user.getAvailableGroups(exp) : null;
      if (groups && groups[gi]) {
        c = colorFromName(groups[gi].name);
        if (c) return c;
      }
    } catch (e) {}
    return Number(gi) === 2 ? 'green' : 'red';
  }

  function labels() {
    return (window.ABCONVERT_LABELS && window.ABCONVERT_LABELS.newPrice) ? window.ABCONVERT_LABELS : null;
  }

  function iconSvg(color) {
    return color === 'green'
      ? '<svg viewBox="0 0 512 512" fill="currentColor" aria-hidden="true"><path d="M.9 0h3.54c25.79 1.96 51.57 4.93 76.81 10.7 12.65 2.39 24.96 6.23 37.32 9.76 25.83 8.13 50.99 19.14 73.15 34.84 21.5 15.21 39.88 35.76 49.43 60.55 10.63 26.95 10.18 57.09 3.69 84.97-.98 4.67-3.02 9.18-3.13 13.99.89 3.23 3.05 5.91 4.72 8.77 5.71 8.98 10.13 18.68 14.67 28.28 4.84 12 9.92 23.94 13.64 36.35 1.3 4.28 2.36 8.63 3.82 12.86 3.46-3.28 5.2-7.8 7.82-11.69 5.87-8.95 12.32-17.55 19.65-25.35 2.12-2.31 4.6-4.38 6.1-7.17.03-2.07-.46-4.09-.88-6.1-5.31-22.06-4.8-46.08 4.46-67.05 8.84-20.33 25.11-36.56 43.5-48.48 13.93-8.95 28.97-16.15 44.64-21.45 22.53-8.21 46.04-13.48 69.71-17.13 12.45-1.71 24.94-3.6 37.54-3.67.16 7.9-.98 15.73-1.73 23.57-2.74 25.14-7.71 50.09-15.64 74.12-6.92 20.82-15.94 41.21-29.27 58.77-10.86 14.53-24.91 26.93-41.5 34.48-14.89 6.78-31.5 9.42-47.78 8.68.2-12.74 3.99-25.11 8.5-36.91 8.9-22.46 22.19-42.94 37.13-61.82.75-1.04 1.47-2.11 2.2-3.17-5.86 3.37-10.91 7.92-15.89 12.44-34.9 31.6-61.12 71.45-83.26 112.68-13.41 21.91-22.11 46.41-27.6 71.42-1.73 9.55 1.06 19.13 1.37 28.69.55 13.69 2.01 27.33 2.11 41.04.22 10.35.54 20.69.51 31.04.05 6.39-2.18 12.91-6.91 17.33-3.29 3.19-7.56 5.07-11.79 6.66h-8.11c-6.43-1.86-12.67-5.5-15.97-11.52-4.36-7.04-2.96-15.62-3.37-23.47-.58-12.98.04-26.01-1.3-38.96-.85-9.88-.72-19.82-2.02-29.66-3.98-42.69-11.26-85.46-26.55-125.67-7.25-17.45-14.49-34.93-23.06-51.79-13-25.72-27.45-50.72-43.43-74.7-8.32-12.21-16.48-24.56-25.64-36.17 16.33 35.85 29.46 73.62 34.08 112.91.71 6.68 1.49 13.4 1.03 20.14-14.43.86-29.02-1.23-42.79-5.58-25.02-8.02-46.3-25.13-62.11-45.84-20.58-26.88-33.69-58.7-43.02-91.02-6.66-23-11.34-46.54-14.65-70.24C2.88 27.01 1.11 13.56.9 0Z"/></svg>'
      : '<svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M21.4 11.6l-9-9A2 2 0 0 0 11 2H4a2 2 0 0 0-2 2v7c0 .5.2 1 .6 1.4l9 9c.8.8 2 .8 2.8 0l7-7c.8-.8.8-2 0-2.8zM6.5 7A1.5 1.5 0 1 1 8 5.5 1.5 1.5 0 0 1 6.5 7z"/></svg>';
  }

  function escapeHtml(s) {
    return String(s == null ? '' : s).replace(/[&<>"]/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c];
    });
  }

  // The "New price" pill. inline=true for PDP (margin-left), false for cards.
  function pillHtml(color, inline) {
    var L = labels();
    if (!L) return '';
    return '<span class="abc-new-price-label abc--' + color + (inline ? ' is-inline' : '') + '">' +
      iconSvg(color) + '<span>' + escapeHtml(L.newPrice) + '</span></span>';
  }

  // PDP-only promo row: "↓ X% niedriger | Dauerhaft günstiger".
  function promoHtml(color, percent) {
    var L = labels();
    if (!L || percent == null) return '';
    var tmpl = L.percentLower || '{{ percent }}% niedriger';
    var pct = tmpl.replace('{{ percent }}', percent).replace('{{percent}}', percent);
    return '<div class="abc-pricepromo abc--' + color + '">' +
      '<span>↓ ' + escapeHtml(pct) + '</span>' +
      '<span class="abc-sep">|</span>' +
      '<span>' + escapeHtml(L.permanentlyCheaper || '') + '</span></div>';
  }

  window.AbcUI = {
    parseMoney: parseMoney,
    formatLikeTemplate: formatLikeTemplate,
    colorForGroupName: colorForGroupName,
    labels: labels,
    pillHtml: pillHtml,
    promoHtml: promoHtml,
    variantInfo: variantInfo
  };

  /* ---------- unit price (Grundpreis) scaling ---------- */

  function findPriceEl(fromEl) {
    var node = fromEl;
    while (node && node !== document.body) {
      var p = node.querySelector && node.querySelector('[data-product-price][data-variant-id]');
      if (p) return p;
      node = node.parentElement;
    }
    return document.querySelector('[data-product-price][data-variant-id]');
  }

  function scaleAll() {
    if (!abcReady()) return;
    document.querySelectorAll('.unit-price__price .money').forEach(function (moneyEl) {
      try {
        var priceEl = findPriceEl(moneyEl);
        if (!priceEl) return;
        var info = variantInfo(priceEl.getAttribute('data-product-id'), priceEl.getAttribute('data-variant-id'));
        if (!info) return;
        var ratio = Number(info.test.price) / Number(info.control.price);
        if (!isFinite(ratio)) return;

        var base = moneyEl.getAttribute('data-abc-orig');
        if (base == null) {
          base = moneyEl.textContent.trim();
          moneyEl.setAttribute('data-abc-orig', base);
        }
        var scaled = ratio === 1 ? base : formatLikeTemplate(base, parseMoney(base) * ratio);
        if (moneyEl.textContent.trim() !== scaled) moneyEl.textContent = scaled;
      } catch (e) {}
    });
  }

  /* ---------- badges (PDP + standard product cards) ---------- */

  // Idempotent innerHTML: compare against the source string we stored (NOT the
  // browser-re-serialised innerHTML, which differs for SVG and would loop).
  function setHtml(el, html) {
    if (el.getAttribute('data-abc-html') !== html) {
      el.innerHTML = html;
      el.setAttribute('data-abc-html', html);
    }
  }

  // One container right after an anchor element; re-rendered idempotently.
  function setBadgeAfter(anchorEl, html) {
    var next = anchorEl.nextElementSibling;
    var box = (next && next.classList && next.classList.contains('abc-badge')) ? next : null;
    if (html) {
      if (!box) {
        box = document.createElement('span');
        box.className = 'abc-badge';
        anchorEl.insertAdjacentElement('afterend', box);
      }
      setHtml(box, html);
    } else if (box) {
      box.remove();
    }
  }

  // Pill on its own centered row, before the price (product cards).
  function setLabelRow(container, html) {
    var existing = container.querySelector(':scope > .abc-label-row');
    if (html) {
      if (!existing) {
        existing = document.createElement('div');
        existing.className = 'abc-label-row';
        var main = container.querySelector(':scope > .main-price');
        if (main) main.insertAdjacentElement('beforebegin', existing);
        else container.insertBefore(existing, container.firstChild);
      }
      setHtml(existing, html);
    } else if (existing) {
      existing.remove();
    }
  }

  function pdpHasCompare(priceEl) {
    var grp = priceEl.closest('.product-price-group') || priceEl.parentElement;
    var cmp = grp && grp.querySelector('#ComparePrice');
    return !!(cmp && cmp.classList.contains('on-sale'));
  }

  function pdpChild(grp, cls, tag) {
    var el = grp.querySelector(':scope > .' + cls);
    if (!el) { el = document.createElement(tag || 'span'); el.className = cls; grp.appendChild(el); }
    return el;
  }
  function pdpRemove(grp, cls) {
    var el = grp.querySelector(':scope > .' + cls);
    if (el) el.remove();
  }

  // PDP: lay out as one line (orig — new — €/kg — pill) + promo on its own line,
  // via flex `order` on .product-price-group. Each piece is a direct child slot.
  function setPdpBadge(priceEl, info, isNew) {
    var grp = priceEl.closest('.product-price-group');
    if (!grp) {
      // Fallback: single combined badge right after the price.
      var html = '';
      if (isNew) {
        if (!pdpHasCompare(priceEl)) {
          html += '<span class="abc-orig-price">' +
            escapeHtml(formatLikeTemplate(priceEl.textContent.trim(), info.control.price)) + '</span>';
        }
        html += pillHtml(info.color, true) + promoHtml(info.color, info.percent);
      }
      setBadgeAfter(priceEl, html);
      return;
    }

    grp.classList.toggle('abc-pdp', !!isNew);

    // "New price" pill goes ABOVE the product title (h1), not in the price line.
    setPdpTitlePill(isNew ? pillHtml(info.color, false) : '');
    pdpRemove(grp, 'abc-pill-slot');

    if (isNew && !pdpHasCompare(priceEl)) {
      var orig = pdpChild(grp, 'abc-orig-price', 'span');
      var t = formatLikeTemplate(priceEl.textContent.trim(), info.control.price);
      if (orig.textContent !== t) orig.textContent = t;
    } else {
      pdpRemove(grp, 'abc-orig-price');
    }

    var pr = isNew ? promoHtml(info.color, info.percent) : '';
    if (pr) { setHtml(pdpChild(grp, 'abc-promo-slot', 'div'), pr); }
    else pdpRemove(grp, 'abc-promo-slot');
  }

  // Pill row placed right before the PDP product title (h1).
  function setPdpTitlePill(html) {
    var title = document.querySelector('.product-details-product-title');
    if (!title || !title.parentElement) return;
    var parent = title.parentElement;
    var row = parent.querySelector(':scope > .abc-title-label');
    if (html) {
      if (!row) { row = document.createElement('div'); row.className = 'abc-title-label'; parent.insertBefore(row, title); }
      else if (row.nextElementSibling !== title) parent.insertBefore(row, title);
      setHtml(row, html);
    } else if (row) {
      row.remove();
    }
  }

  function applyBadges() {
    if (!labels() || !abcReady()) return;
    document.querySelectorAll(
      '[data-product-price][data-variant-id], .price--sale-price[data-variant-id], .price--regular-price[data-variant-id]'
    ).forEach(function (priceEl) {
      try {
        // Size-pill cards manage their own price/badge in grid-view-item.js.
        var card = priceEl.closest('product-card');
        if (card && card.querySelector('.size-pill')) return;

        var info = variantInfo(priceEl.getAttribute('data-product-id'), priceEl.getAttribute('data-variant-id'));
        var isNew = !!(info && info.isNew);

        // Price colour (always red when a test price is shown).
        priceEl.classList.toggle('abc-price--new', isNew);

        // The promo banner ("↓ X% niedriger | Dauerhaft günstiger") is PDP-only.
        // Everything else (collection cards, recommendation/cross-sell sections,
        // basic cards) gets the pill + struck original only.
        var isPdp = priceEl.hasAttribute('data-product-price') && !!priceEl.closest('.product-price-group');
        var container = priceEl.closest('.product-grid--price, .product-card-basic__price');
        var origStr = (isNew && !(priceEl.parentElement && priceEl.parentElement.querySelector('.price--compare-at-price')))
          ? '<span class="abc-orig-price">' + escapeHtml(formatLikeTemplate(priceEl.textContent.trim(), info.control.price)) + '</span>'
          : '';

        if (isPdp) {
          /* ---- PDP: new — old / promo banner / €kg, pill above the title ---- */
          setPdpBadge(priceEl, info || { control: {}, color: 'red', percent: null }, isNew);
        } else if (container) {
          /* ---- known card container: prices → pill → unit price (CSS order) ---- */
          container.classList.toggle('abc-has-badge', isNew);
          setLabelRow(container, isNew ? pillHtml(info.color, false) : '');
          setBadgeAfter(priceEl, origStr);
        } else {
          /* ---- other card-like (recommendations/cross-sell): pill below price ---- */
          var html = origStr;
          if (isNew) html += '<span class="abc-badge-pill">' + pillHtml(info.color, false) + '</span>';
          setBadgeAfter(priceEl, html);
        }
      } catch (e) {}
    });
  }

  // True when the visitor is in a NON-control group of the price experiment.
  function abcNonControl() {
    var dict = window.abconvertPriceCartTransformTestDict || {};
    var pid = Object.keys(dict)[0];
    if (!pid) return false;
    var gi = abcGroupIndex(pid);
    return gi != null && Number(gi) !== 0;
  }

  // Homepage banner text swap for non-control groups (e.g. "Spare jetzt 20%" →
  // "Jetzt 20 % zusätzlich sparen"). Find/replace strings come from theme settings.
  function swapBanner() {
    var cfg = window.ABCONVERT_BANNER_SWAP;
    if (!cfg || !cfg.find || !cfg.replace) return;
    if (!abcReady() || !abcNonControl()) return;
    document.querySelectorAll('.featured-row__subtext').forEach(function (el) {
      try {
        if (el.getAttribute('data-abc-swapped') === '1') return;
        if (el.textContent.indexOf(cfg.find) === -1) return;
        el.innerHTML = el.innerHTML.split(cfg.find).join(cfg.replace);
        el.setAttribute('data-abc-swapped', '1');
      } catch (e) {}
    });
  }

  var domObserver = null;

  function sync() {
    scaleAll();
    applyBadges();
    swapBanner();
  }

  // Run sync with the observer disconnected so our own DOM writes don't
  // re-trigger it (prevents the update loop / flicker).
  function syncSafe() {
    if (domObserver) domObserver.disconnect();
    try { sync(); } finally {
      if (domObserver) domObserver.observe(document.body, { childList: true, subtree: true });
    }
  }

  /* ---------- triggers ---------- */

  function onVariantUpdated() {
    setTimeout(syncSafe, 0);
    setTimeout(syncSafe, 300);
  }

  function bindEventBus() {
    if (window.eventBus && typeof window.eventBus.on === 'function') {
      window.eventBus.on('variant:updated', onVariantUpdated);
      return true;
    }
    return false;
  }

  function whenAbcReady(cb) {
    if (abcReady()) return cb();
    var tries = 0;
    var t = setInterval(function () {
      if (abcReady() || tries++ > 40) {
        clearInterval(t);
        if (abcReady()) cb();
      }
    }, 200);
  }

  var PRICE_SEL = '.price--sale-price,.price--regular-price,[data-product-price],.unit-price__price';

  // Only react when a price element is actually (re)inserted — ignore unrelated
  // page churn (cart drawer, animations, the ABConvert preview panel) and our
  // own injected `.abc-*` nodes. Debounced.
  function isRelevant(records) {
    for (var i = 0; i < records.length; i++) {
      var added = records[i].addedNodes;
      for (var j = 0; j < added.length; j++) {
        var n = added[j];
        if (n.nodeType !== 1) continue;
        if (n.className && typeof n.className === 'string' && n.className.indexOf('abc-') === 0) continue;
        if ((n.matches && n.matches(PRICE_SEL)) || (n.querySelector && n.querySelector(PRICE_SEL))) return true;
      }
    }
    return false;
  }

  // Re-run on dynamically injected content (AJAX collections, predictive search,
  // quick view, recommendation/cart sections).
  function observeDynamic() {
    if (!('MutationObserver' in window)) return;
    var timer;
    domObserver = new MutationObserver(function (records) {
      if (!isRelevant(records)) return;
      clearTimeout(timer);
      timer = setTimeout(syncSafe, 200);
    });
    domObserver.observe(document.body, { childList: true, subtree: true });
  }

  function init() {
    whenAbcReady(syncSafe);
    if (!bindEventBus()) {
      var tries = 0;
      var t = setInterval(function () {
        if (bindEventBus() || tries++ > 40) clearInterval(t);
      }, 200);
    }
    observeDynamic();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
